'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BacCalculator() {
  const t = useTranslations("tools.bacCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    gender: 'male',
    weight: 155,
    weightUnit: 'lbs',
    hours: 2,
    minutes: 0,
    beerAmount: 2,
    beerSize: 330,
    beerABV: 5,
    wineAmount: 0,
    wineSize: 150,
    wineABV: 12,
    liquorAmount: 0,
    liquorSize: 50,
    liquorABV: 40,
  });

  const [results, setResults] = useState<{
    bac: number;
    totalAlcohol: number;
    standardDrinks: number;
    totalTime: number;
    status: 'safe' | 'caution' | 'danger' | 'severe';
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let weight = inputs.weight;
    if (inputs.weightUnit === 'lbs') {
      weight = weight * 0.453592;
    }

    const totalTime = inputs.hours + (inputs.minutes / 60);

    const beerAlcohol = (inputs.beerAmount * inputs.beerSize * inputs.beerABV / 100) * 0.789;
    const wineAlcohol = (inputs.wineAmount * inputs.wineSize * inputs.wineABV / 100) * 0.789;
    const liquorAlcohol = (inputs.liquorAmount * inputs.liquorSize * inputs.liquorABV / 100) * 0.789;

    const totalAlcoholGrams = beerAlcohol + wineAlcohol + liquorAlcohol;

    const r = inputs.gender === 'male' ? 0.68 : 0.55;

    const widmarkFormula = (totalAlcoholGrams / (weight * 1000 * r)) * 100;

    const metabolismRate = 0.015;
    const bacAfterTime = widmarkFormula - (metabolismRate * totalTime);

    const finalBAC = Math.max(0, bacAfterTime);

    const standardDrinks = totalAlcoholGrams / 14;

    let status: 'safe' | 'caution' | 'danger' | 'severe' = 'safe';
    if (finalBAC >= 0.15) {
      status = 'severe';
    } else if (finalBAC >= 0.08) {
      status = 'danger';
    } else if (finalBAC >= 0.03) {
      status = 'caution';
    }

    setResults({
      bac: finalBAC,
      totalAlcohol: totalAlcoholGrams,
      standardDrinks,
      totalTime,
      status,
    });

    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    safe: '#10b981',
    caution: '#f59e0b',
    danger: '#ef4444',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '0 auto' },
    { fontSize: '1rem', maxWidth: '100%' },
    { fontSize: '1.125rem', maxWidth: '700px' },
    { fontSize: '1.25rem', maxWidth: '800px' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' as const },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', textAlign: 'center' as const },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem',
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.7rem' },
    { padding: '0.725rem' },
    { padding: '0.75rem' }
  );

  const selectStyle = { ...inputStyle };

  const radioGroupStyle = {
    display: 'flex',
    gap: '1.5rem',
    marginTop: '0.5rem',
  };

  const radioOptionStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  };

  const radioInputStyle = {
    width: '1.25rem',
    height: '1.25rem',
    marginRight: '0.5rem',
    cursor: 'pointer',
    accentColor: '#2563eb',
  };

  const radioLabelStyle = {
    margin: 0,
    cursor: 'pointer',
    fontWeight: 500,
  };

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const sectionTitleStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, margin: '2rem 0 1rem 0', paddingBottom: '0.5rem', borderBottom: '2px solid #e2e8f0' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const drinkRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', alignItems: 'end', marginBottom: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '0.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr 1.5fr 1fr' },
    { gridTemplateColumns: '1fr 1fr 1.5fr 1fr' }
  );

  const drinkLabelStyle = {
    fontWeight: 600,
    color: '#475569',
    paddingBottom: '0.75rem',
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.95rem 1.75rem', fontSize: '1.05rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderTop: '3px solid #2563eb' },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.textPrimary },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const bacDisplayStyle = getResponsiveStyle(
    { textAlign: 'center' as const, background: 'white', borderRadius: '1rem', marginBottom: '1.5rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)' },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const bacLabelStyle = getResponsiveStyle(
    { color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '1px' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const bacValueStyle = getResponsiveStyle(
    { fontWeight: 700, margin: '0.5rem 0' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' },
    { fontSize: '3.5rem' }
  );

  const statusBadgeStyle = getResponsiveStyle(
    { display: 'inline-block', padding: '0.5rem 1rem', borderRadius: '2rem', fontWeight: 600, marginTop: '1rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultCardStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const resultCardLabelStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  };

  const resultCardValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const warningBoxStyle = getResponsiveStyle(
    { background: '#fef3c7', borderLeft: '4px solid #f59e0b', borderRadius: '0.5rem', marginTop: '1.5rem' },
    { padding: '1rem 1.25rem' },
    { padding: '1rem 1.375rem' },
    { padding: '1rem 1.5rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    { background: '#e0f2fe', borderLeft: '4px solid #2563eb', borderRadius: '0.5rem', marginTop: '1rem' },
    { padding: '1rem 1.25rem' },
    { padding: '1rem 1.375rem' },
    { padding: '1rem 1.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
  };

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'safe': return colors.safe;
      case 'caution': return colors.caution;
      case 'danger': return colors.danger;
      case 'severe': return colors.danger;
      default: return colors.safe;
    }
  };

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case 'safe': return '#d1fae5';
      case 'caution': return '#fef3c7';
      case 'danger': return '#fee2e2';
      case 'severe': return '#fee2e2';
      default: return '#d1fae5';
    }
  };

  const getStatusTextColor = (status: string) => {
    switch (status) {
      case 'safe': return '#065f46';
      case 'caution': return '#92400e';
      case 'danger': return '#991b1b';
      case 'severe': return '#991b1b';
      default: return '#065f46';
    }
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🍺 {t("toolTitle")}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>{t("genderLabel")}</label>
              <div style={radioGroupStyle}>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="male"
                    name="gender"
                    value="male"
                    checked={inputs.gender === 'male'}
                    onChange={(e) => setInputs({...inputs, gender: e.target.value})}
                    style={radioInputStyle}
                  />
                  <label htmlFor="male" style={radioLabelStyle}>{t("male")}</label>
                </div>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="female"
                    name="gender"
                    value="female"
                    checked={inputs.gender === 'female'}
                    onChange={(e) => setInputs({...inputs, gender: e.target.value})}
                    style={radioInputStyle}
                  />
                  <label htmlFor="female" style={radioLabelStyle}>{t("female")}</label>
                </div>
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="weight" style={labelStyle}>{t("weightLabel")}</label>
                <input
                  type="number"
                  id="weight"
                  min="1"
                  step="0.1"
                  value={inputs.weight}
                  onChange={(e) => setInputs({...inputs, weight: parseFloat(e.target.value)})}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={formGroupStyle}>
                <label htmlFor="weight-unit" style={labelStyle}>{t("weightUnitLabel")}</label>
                <select
                  id="weight-unit"
                  value={inputs.weightUnit}
                  onChange={(e) => setInputs({...inputs, weightUnit: e.target.value})}
                  style={selectStyle}
                >
                  <option value="lbs">{t("lbs")}</option>
                  <option value="kg">{t("kg")}</option>
                </select>
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="hours" style={labelStyle}>{t("hoursLabel")}</label>
                <input
                  type="number"
                  id="hours"
                  min="0"
                  step="0.1"
                  value={inputs.hours}
                  onChange={(e) => setInputs({...inputs, hours: parseFloat(e.target.value)})}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={formGroupStyle}>
                <label htmlFor="minutes" style={labelStyle}>{t("minutesLabel")}</label>
                <input
                  type="number"
                  id="minutes"
                  min="0"
                  max="59"
                  value={inputs.minutes}
                  onChange={(e) => setInputs({...inputs, minutes: parseFloat(e.target.value)})}
                  required
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={sectionTitleStyle}>{t("alcoholConsumedTitle")}</div>

            <div style={drinkRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>🍺 {t("beer")}</label>
                <div style={drinkLabelStyle}>{t("amount")}</div>
              </div>
              <div style={formGroupStyle}>
                <input
                  type="number"
                  id="beer-amount"
                  min="0"
                  step="0.1"
                  value={inputs.beerAmount}
                  onChange={(e) => setInputs({...inputs, beerAmount: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                />
              </div>
              <div style={formGroupStyle}>
                <select
                  id="beer-size"
                  value={inputs.beerSize}
                  onChange={(e) => setInputs({...inputs, beerSize: parseFloat(e.target.value)})}
                  style={selectStyle}
                >
                  <option value="330">{t("beer12oz")}</option>
                  <option value="250">{t("beer250ml")}</option>
                  <option value="500">{t("beer16oz")}</option>
                  <option value="600">{t("beer20oz")}</option>
                  <option value="1000">{t("beer1L")}</option>
                </select>
              </div>
              <div style={formGroupStyle}>
                <select
                  id="beer-abv"
                  value={inputs.beerABV}
                  onChange={(e) => setInputs({...inputs, beerABV: parseFloat(e.target.value)})}
                  style={selectStyle}
                >
                  <option value="4.2">{t("beerLight")}</option>
                  <option value="5">{t("beer5")}</option>
                  <option value="5.8">{t("beer58")}</option>
                </select>
              </div>
            </div>

            <div style={drinkRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>🍷 {t("wine")}</label>
                <div style={drinkLabelStyle}>{t("amount")}</div>
              </div>
              <div style={formGroupStyle}>
                <input
                  type="number"
                  id="wine-amount"
                  min="0"
                  step="0.1"
                  value={inputs.wineAmount}
                  onChange={(e) => setInputs({...inputs, wineAmount: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                />
              </div>
              <div style={formGroupStyle}>
                <select
                  id="wine-size"
                  value={inputs.wineSize}
                  onChange={(e) => setInputs({...inputs, wineSize: parseFloat(e.target.value)})}
                  style={selectStyle}
                >
                  <option value="150">{t("wine5oz")}</option>
                  <option value="60">{t("wine2oz")}</option>
                  <option value="250">{t("wine8oz")}</option>
                  <option value="750">{t("wine750ml")}</option>
                </select>
              </div>
              <div style={formGroupStyle}>
                <select
                  id="wine-abv"
                  value={inputs.wineABV}
                  onChange={(e) => setInputs({...inputs, wineABV: parseFloat(e.target.value)})}
                  style={selectStyle}
                >
                  <option value="10">{t("wine10")}</option>
                  <option value="12">{t("wine12")}</option>
                  <option value="14">{t("wine14")}</option>
                  <option value="16">{t("wine16")}</option>
                </select>
              </div>
            </div>

            <div style={drinkRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>🥃 {t("liquor")}</label>
                <div style={drinkLabelStyle}>{t("amount")}</div>
              </div>
              <div style={formGroupStyle}>
                <input
                  type="number"
                  id="liquor-amount"
                  min="0"
                  step="0.1"
                  value={inputs.liquorAmount}
                  onChange={(e) => setInputs({...inputs, liquorAmount: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                />
              </div>
              <div style={formGroupStyle}>
                <select
                  id="liquor-size"
                  value={inputs.liquorSize}
                  onChange={(e) => setInputs({...inputs, liquorSize: parseFloat(e.target.value)})}
                  style={selectStyle}
                >
                  <option value="50">{t("liquor15oz")}</option>
                  <option value="30">{t("liquor1oz")}</option>
                  <option value="100">{t("liquor100ml")}</option>
                  <option value="750">{t("liquor750ml")}</option>
                </select>
              </div>
              <div style={formGroupStyle}>
                <select
                  id="liquor-abv"
                  value={inputs.liquorABV}
                  onChange={(e) => setInputs({...inputs, liquorABV: parseFloat(e.target.value)})}
                  style={selectStyle}
                >
                  <option value="35">{t("liquor35")}</option>
                  <option value="40">{t("liquor40")}</option>
                  <option value="45">{t("liquor45")}</option>
                  <option value="50">{t("liquor50")}</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <h3 style={resultsH3Style}>{t("resultsTitle")}</h3>

              <div style={bacDisplayStyle}>
                <div style={bacLabelStyle}>{t("bacLabel")}</div>
                <div style={{...bacValueStyle, color: getStatusColor(results.status)}}>
                  {results.bac.toFixed(3)}%
                </div>
                <div style={{
                  ...statusBadgeStyle,
                  background: getStatusBgColor(results.status),
                  color: getStatusTextColor(results.status),
                }}>
                  {t(`status${results.status.charAt(0).toUpperCase() + results.status.slice(1)}`)}
                </div>
              </div>

              <div style={resultGridStyle}>
                <div style={resultCardStyle}>
                  <div style={resultCardLabelStyle}>{t("totalAlcoholLabel")}</div>
                  <div style={resultCardValueStyle}>{results.totalAlcohol.toFixed(1)}g</div>
                </div>
                <div style={resultCardStyle}>
                  <div style={resultCardLabelStyle}>{t("standardDrinksLabel")}</div>
                  <div style={resultCardValueStyle}>{results.standardDrinks.toFixed(1)}</div>
                </div>
                <div style={resultCardStyle}>
                  <div style={resultCardLabelStyle}>{t("timeElapsedLabel")}</div>
                  <div style={resultCardValueStyle}>
                    {Math.floor(results.totalTime)}h {Math.round((results.totalTime - Math.floor(results.totalTime)) * 60)}m
                  </div>
                </div>
              </div>

              {results.status === 'safe' && (
                <div style={infoBoxStyle}>
                  <p style={{ margin: 0, color: '#1e40af', fontSize: '0.9rem' }}>
                    <strong>{t("safeTitle")}</strong> {t("safeMessage")}
                  </p>
                </div>
              )}

              {results.status === 'caution' && (
                <div style={warningBoxStyle}>
                  <p style={{ margin: 0, color: '#92400e', fontWeight: 500 }}>
                    <strong>{t("cautionTitle")}</strong> {t("cautionMessage")}
                  </p>
                </div>
              )}

              {results.status === 'danger' && (
                <div style={warningBoxStyle}>
                  <p style={{ margin: 0, color: '#92400e', fontWeight: 500 }}>
                    <strong>{t("dangerTitle")}</strong> {t("dangerMessage")}
                  </p>
                </div>
              )}

              {results.status === 'severe' && (
                <div style={warningBoxStyle}>
                  <p style={{ margin: 0, color: '#92400e', fontWeight: 500 }}>
                    <strong>{t("severeTitle")}</strong> {t("severeMessage")}
                  </p>
                </div>
              )}

              <div style={infoBoxStyle}>
                <p style={{ margin: 0, color: '#1e40af', fontSize: '0.9rem' }}>
                  <strong>{t("disclaimerTitle")}</strong> {t("disclaimerMessage")}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToStep1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToStep2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToStep3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToStep4")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph3")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph4")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t("widmarkFormulaTitle")}</h3>
        <p style={paragraphStyle}>{t("widmarkFormulaParagraph")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t("factorsTitle")}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("factorGender")}</strong> {t("factorGenderDesc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("factorWeight")}</strong> {t("factorWeightDesc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("factorTime")}</strong> {t("factorTimeDesc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("factorAmount")}</strong> {t("factorAmountDesc")}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem'}}>{t("bacLevelsTitle")}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("bac003")}</strong> {t("bac003Desc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("bac008")}</strong> {t("bac008Desc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("bac015")}</strong> {t("bac015Desc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("bac030")}</strong> {t("bac030Desc")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem'}}>{t("faq1Question")}</h3>
        <p style={paragraphStyle}>{t("faq1Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem'}}>{t("faq2Question")}</h3>
        <p style={paragraphStyle}>{t("faq2Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem'}}>{t("faq3Question")}</h3>
        <p style={paragraphStyle}>{t("faq3Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem'}}>{t("faq4Question")}</h3>
        <p style={paragraphStyle}>{t("faq4Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem'}}>{t("faq5Question")}</h3>
        <p style={paragraphStyle}>{t("faq5Answer")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://sites.duke.edu/apep/module-1-gender-matters/class-activity-is-he-drunk-calculate-the-bac/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref1")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://en.wikipedia.org/wiki/Blood_alcohol_content" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref2")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.law.cornell.edu/wex/blood-alcohol_content_(bac)" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref3")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://case.edu/studentlife/healthcounseling/student-wellnessalcohol-and-drugs/blood-alcohol-concentration" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref4")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://alcohol.org/health-effects/blood-alcohol-concentration/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref5")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://my.clevelandclinic.org/health/diagnostics/22689-blood-alcohol-content-bac" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref6")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://super.stanford.edu/alcohol-drug-info/reduce-your-risk/what-blood-alcohol-concentration-bac" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref7")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.csbsju.edu/well-being-center/health-promotion/alcohol-guide/understanding-blood-alcohol-content-bac/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("ref8")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}